/**
 * Centralized Design Token System
 *
 * This file contains all color definitions for the entire application.
 * All colors are organized by theme mode (light, dark, colorblind) and usage context (UI, charts, tables).
 *
 * Usage:
 * - Import the appropriate theme object based on current mode
 * - For Chart.js: Use CHART_COLORS.light/dark/colorblind
 * - For UI components: Use UI_COLORS.light/dark/colorblind
 * - For CSS: Use the corresponding CSS variables defined in styles.scss
 */

// ============================================================================
// BASE COLOR PALETTE
// ============================================================================

/**
 * Traton primary color scale (teal/green)
 */
export const TRATON_PRIMARY = {
  50: '#EDF6F8',
  100: '#DAEEF1',
  200: '#B2DBE1',
  300: '#8DCAD3',
  400: '#66B8C4',
  500: '#46A6B4',
  600: '#378490',
  700: '#29626B',
  800: '#1B4046',
  900: '#0E2225',
  950: '#071112',
} as const;

/**
 * Surface colors for backgrounds and containers
 */
export const TRATON_SURFACE = {
  0: '#FFFFFF',
  50: '#F6F8F8',
  100: '#EDF1F2',
  200: '#DCE4E5',
  300: '#C7D4D6',
  400: '#B6C6C9',
  500: '#90A8AD',
  600: '#68878D',
  700: '#4C6367',
  800: '#303E41',
  900: '#111717',
  950: '#040606',
} as const;

/**
 * Standard semantic colors (normal view)
 */
export const STANDARD_SEMANTIC = {
  success: {
    50: '#ECFDF5',
    100: '#D1FAE5',
    200: '#A7F3D0',
    300: '#6EE7B7',
    400: '#34D399',
    500: '#10B981',
    600: '#059669',
    700: '#047857',
    800: '#065F46',
    900: '#064E3B',
    950: '#022C22',
  },
  warning: {
    50: '#FEFCE8',
    100: '#FEF9C3',
    200: '#FEF08A',
    300: '#FDE047',
    400: '#FBBF24',
    500: '#F59E0B',
    600: '#D97706',
    700: '#B45309',
    800: '#92400E',
    900: '#78350F',
    950: '#451A03',
  },
  danger: {
    50: '#FEF2F2',
    100: '#FEE2E2',
    200: '#FECACA',
    300: '#FCA5A5',
    400: '#F87171',
    500: '#EF4444',
    600: '#DC2626',
    700: '#B91C1C',
    800: '#991B1B',
    900: '#7F1D1D',
    950: '#450A0A',
  },
  info: {
    50: '#EFF6FF',
    100: '#DBEAFE',
    200: '#BFDBFE',
    300: '#93C5FD',
    400: '#60A5FA',
    500: '#3B82F6',
    600: '#2563EB',
    700: '#1D4ED8',
    800: '#1E40AF',
    900: '#1E3A8A',
    950: '#172554',
  },
  secondary: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
    950: '#030712',
  },
} as const;

/**
 * Colorblind-friendly semantic colors
 * Based on research for deuteranopia, protanopia, and tritanopia
 */
export const COLORBLIND_SEMANTIC = {
  success: {
    50: '#E0F7F4',
    100: '#B3EDE5',
    200: '#80E2D4',
    300: '#4DD7C3',
    400: '#26CFB6',
    500: '#009E73',
    600: '#00916A',
    700: '#00825F',
    800: '#007354',
    900: '#005A42',
    950: '#003D2C',
  },
  warning: {
    50: '#FEFCE8',
    100: '#FEF9C3',
    200: '#FEF08A',
    300: '#FDE047',
    400: '#F0E442',
    500: '#EAB308',
    600: '#CA8A04',
    700: '#A16207',
    800: '#854D0E',
    900: '#713F12',
    950: '#422006',
  },
  danger: {
    50: '#FEF3E8',
    100: '#FCE0C5',
    200: '#FACB9E',
    300: '#F8B677',
    400: '#F6A659',
    500: '#D55E00',
    600: '#C35500',
    700: '#B04C00',
    800: '#9D4300',
    900: '#7D3600',
    950: '#5A2600',
  },
  info: {
    50: '#E6F2FB',
    100: '#BFDFF6',
    200: '#95CBF0',
    300: '#6BB7EA',
    400: '#4CA8E6',
    500: '#0072B2',
    600: '#006AA3',
    700: '#005F91',
    800: '#00547F',
    900: '#00405F',
    950: '#002B40',
  },
  secondary: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
    950: '#030712',
  },
} as const;

// ============================================================================
// UI COLORS - For PrimeNG components, tags, badges, buttons, etc.
// ============================================================================

/**
 * UI Colors for Light Mode (Standard)
 */
export const UI_COLORS_LIGHT = {
  success: STANDARD_SEMANTIC.success,
  warning: STANDARD_SEMANTIC.warning,
  danger: STANDARD_SEMANTIC.danger,
  info: STANDARD_SEMANTIC.info,
  secondary: STANDARD_SEMANTIC.secondary,
  primary: TRATON_PRIMARY,
  surface: TRATON_SURFACE,
} as const;

/**
 * UI Colors for Dark Mode (Standard)
 */
export const UI_COLORS_DARK = {
  success: STANDARD_SEMANTIC.success,
  warning: STANDARD_SEMANTIC.warning,
  danger: STANDARD_SEMANTIC.danger,
  info: STANDARD_SEMANTIC.info,
  secondary: STANDARD_SEMANTIC.secondary,
  primary: TRATON_PRIMARY,
  surface: TRATON_SURFACE,
} as const;

/**
 * UI Colors for Colorblind Mode
 */
export const UI_COLORS_COLORBLIND = {
  success: COLORBLIND_SEMANTIC.success,
  warning: COLORBLIND_SEMANTIC.warning,
  danger: COLORBLIND_SEMANTIC.danger,
  info: COLORBLIND_SEMANTIC.info,
  secondary: COLORBLIND_SEMANTIC.secondary,
  primary: TRATON_PRIMARY,
  surface: TRATON_SURFACE,
} as const;

// ============================================================================
// CHART COLORS - For Chart.js charts (donut, bar, line, etc.)
// ============================================================================

/**
 * Chart Colors for Standard View (Light/Dark mode)
 * Used for Chart.js backgroundColor and hoverBackgroundColor
 */
export const CHART_COLORS_STANDARD = {
  success: {
    primary: STANDARD_SEMANTIC.success[400],
    hover: STANDARD_SEMANTIC.success[500],
    border: STANDARD_SEMANTIC.success[600],
  },
  warning: {
    primary: STANDARD_SEMANTIC.warning[400],
    hover: STANDARD_SEMANTIC.warning[500],
    border: STANDARD_SEMANTIC.warning[600],
  },
  danger: {
    primary: STANDARD_SEMANTIC.danger[400],
    hover: STANDARD_SEMANTIC.danger[500],
    border: STANDARD_SEMANTIC.danger[600],
  },
  info: {
    primary: STANDARD_SEMANTIC.info[400],
    hover: STANDARD_SEMANTIC.info[500],
    border: STANDARD_SEMANTIC.info[600],
  },
  secondary: {
    primary: STANDARD_SEMANTIC.secondary[400],
    hover: STANDARD_SEMANTIC.secondary[500],
    border: STANDARD_SEMANTIC.secondary[600],
  },
} as const;

/**
 * Chart Colors for Colorblind Mode
 * Used for Chart.js backgroundColor and hoverBackgroundColor
 */
export const CHART_COLORS_COLORBLIND = {
  success: {
    primary: COLORBLIND_SEMANTIC.success[400],
    hover: COLORBLIND_SEMANTIC.success[500],
    border: COLORBLIND_SEMANTIC.success[600],
  },
  warning: {
    primary: COLORBLIND_SEMANTIC.warning[400],
    hover: COLORBLIND_SEMANTIC.warning[500],
    border: COLORBLIND_SEMANTIC.warning[600],
  },
  danger: {
    primary: COLORBLIND_SEMANTIC.danger[400],
    hover: COLORBLIND_SEMANTIC.danger[500],
    border: COLORBLIND_SEMANTIC.danger[600],
  },
  info: {
    primary: COLORBLIND_SEMANTIC.info[400],
    hover: COLORBLIND_SEMANTIC.info[500],
    border: COLORBLIND_SEMANTIC.info[600],
  },
  secondary: {
    primary: COLORBLIND_SEMANTIC.secondary[400],
    hover: COLORBLIND_SEMANTIC.secondary[500],
    border: COLORBLIND_SEMANTIC.secondary[600],
  },
} as const;

// ============================================================================
// TABLE COLORS - For TreeTables, DataTables, status rows, etc.
// ============================================================================

/**
 * Table Row Background Colors for Standard View
 */
export const TABLE_COLORS_STANDARD = {
  passed: STANDARD_SEMANTIC.success[100],
  failed: STANDARD_SEMANTIC.danger[100],
  skipped: STANDARD_SEMANTIC.info[100],
  warning: STANDARD_SEMANTIC.warning[100],
  mixed: STANDARD_SEMANTIC.warning[100],
  hover: TRATON_SURFACE[100],
  hoverClickable: STANDARD_SEMANTIC.info[100],
} as const;

/**
 * Table Row Background Colors for Colorblind Mode
 */
export const TABLE_COLORS_COLORBLIND = {
  passed: COLORBLIND_SEMANTIC.success[100],
  failed: COLORBLIND_SEMANTIC.danger[100],
  skipped: COLORBLIND_SEMANTIC.info[100],
  warning: COLORBLIND_SEMANTIC.warning[100],
  mixed: COLORBLIND_SEMANTIC.warning[100],
  hover: TRATON_SURFACE[100],
  hoverClickable: COLORBLIND_SEMANTIC.info[100],
} as const;

// ============================================================================
// CONVENIENCE EXPORTS - Pre-configured theme objects
// ============================================================================

/**
 * Complete theme configuration for Light Mode (Standard)
 */
export const LIGHT_THEME = {
  ui: UI_COLORS_LIGHT,
  chart: CHART_COLORS_STANDARD,
  table: TABLE_COLORS_STANDARD,
} as const;

/**
 * Complete theme configuration for Dark Mode (Standard)
 */
export const DARK_THEME = {
  ui: UI_COLORS_DARK,
  chart: CHART_COLORS_STANDARD,
  table: TABLE_COLORS_STANDARD,
} as const;

/**
 * Complete theme configuration for Colorblind Mode
 */
export const COLORBLIND_THEME = {
  ui: UI_COLORS_COLORBLIND,
  chart: CHART_COLORS_COLORBLIND,
  table: TABLE_COLORS_COLORBLIND,
} as const;

// ============================================================================
// HELPER TYPES
// ============================================================================

export type ThemeMode = 'light' | 'dark' | 'colorblind';
export type SemanticColor = 'success' | 'warning' | 'danger' | 'info' | 'secondary';
export type ColorScale = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;

/**
 * Gets the appropriate theme based on mode
 */
export function getTheme(mode: ThemeMode) {
  switch (mode) {
    case 'light':
      return LIGHT_THEME;
    case 'dark':
      return DARK_THEME;
    case 'colorblind':
      return COLORBLIND_THEME;
  }
}

type ChartColorPair = { primary: string; hover: string };

/**
 * Distinct categorical colors for pie/doughnut charts (one unique color per slice).
 */
export const CHART_CATEGORICAL_PALETTE_STANDARD: readonly ChartColorPair[] = [
  CHART_COLORS_STANDARD.info,
  CHART_COLORS_STANDARD.success,
  CHART_COLORS_STANDARD.warning,
  CHART_COLORS_STANDARD.danger,
  CHART_COLORS_STANDARD.secondary,
  { primary: TRATON_PRIMARY[400], hover: TRATON_PRIMARY[500] },
  { primary: '#A78BFA', hover: '#8B5CF6' },
  { primary: '#FB923C', hover: '#F97316' },
  { primary: '#F472B6', hover: '#EC4899' },
  { primary: '#2DD4BF', hover: '#14B8A6' },
  { primary: '#818CF8', hover: '#6366F1' },
  { primary: '#E879F9', hover: '#D946EF' },
  { primary: '#4ADE80', hover: '#22C55E' },
  { primary: '#FACC15', hover: '#EAB308' },
  { primary: '#FB7185', hover: '#F43F5E' },
  { primary: '#38BDF8', hover: '#0EA5E9' },
];

/**
 * Colorblind-friendly categorical palette (Wong-inspired extensions).
 */
export const CHART_CATEGORICAL_PALETTE_COLORBLIND: readonly ChartColorPair[] = [
  CHART_COLORS_COLORBLIND.info,
  CHART_COLORS_COLORBLIND.success,
  CHART_COLORS_COLORBLIND.warning,
  CHART_COLORS_COLORBLIND.danger,
  CHART_COLORS_COLORBLIND.secondary,
  { primary: '#56B4E9', hover: '#0072B2' },
  { primary: '#E69F00', hover: '#D55E00' },
  { primary: '#CC79A7', hover: '#AA4499' },
  { primary: '#44AA99', hover: '#117733' },
  { primary: '#882255', hover: '#661100' },
  { primary: '#332288', hover: '#1F1A5C' },
  { primary: '#DDCC77', hover: '#C4A84A' },
  { primary: '#999999', hover: '#777777' },
  { primary: '#000000', hover: '#333333' },
  { primary: '#88CCEE', hover: '#56B4E9' },
  { primary: '#AA4499', hover: '#882255' },
  { primary: '#117733', hover: '#004D26' },
];

/**
 * Returns unique background/hover colors for categorical chart slices.
 */
export function getCategoricalChartPalette(
  isColorblind: boolean,
  count: number,
): { bg: string[]; hover: string[] } {
  const palette = isColorblind
    ? CHART_CATEGORICAL_PALETTE_COLORBLIND
    : CHART_CATEGORICAL_PALETTE_STANDARD;
  const bg: string[] = [];
  const hover: string[] = [];
  for (let i = 0; i < count; i++) {
    if (i < palette.length) {
      bg.push(palette[i].primary);
      hover.push(palette[i].hover);
    } else {
      const hue = (i * 137.508) % 360;
      const sat = isColorblind ? 65 : 55;
      bg.push(`hsl(${hue}, ${sat}%, 55%)`);
      hover.push(`hsl(${hue}, ${sat}%, 45%)`);
    }
  }
  return { bg, hover };
}

/**
 * Gets chart colors based on colorblind mode
 */
export function getChartColors(isColorblind: boolean) {
  return isColorblind ? CHART_COLORS_COLORBLIND : CHART_COLORS_STANDARD;
}

/**
 * Gets table colors based on colorblind mode
 */
export function getTableColors(isColorblind: boolean) {
  return isColorblind ? TABLE_COLORS_COLORBLIND : TABLE_COLORS_STANDARD;
}
